import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/global";


const FIXED_CUSTOM_ATTRIBUTES = {
  amazon: [
    { label: "Product Tag 1", key: "VariantAnalysisTablePPMPlaceholder.product_tags", value: "" },
    ...[2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => ({
      label: `Product Tag ${index}`,
      key: `VariantAnalysisTablePPMPlaceholder.product_tags_${index}`,
      value: "",
    })),
  ],
  flipkart: [
    { label: "Product Tag 1", key: "product_tags", value: "" },
    ...[2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => ({
      label: `Product Tag ${index}`,
      key: `product_tags_${index}`,
      value: "",
    })),
  ],
  myntra: [
    { label: "Product Tag 1", key: "product_tags", value: "" },
    ...[2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => ({
      label: `Product Tag ${index}`,
      key: `product_tags_${index}`,
      value: "",
    })),
  ],
}

export default function FeedMapping() {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const { clientId } = useGlobalContext();
  const userPlatform = JSON.parse(localStorage.getItem("user_platform")) || "flipkart";

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [mappingExists, setMappingExists] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState({});

  const baseUrl = process.env.REACT_APP_ADS_API
  const endpoint = `${baseUrl}/client/${clientId}/custom-attribute-definition?source=${userPlatform}&entity_type=PRODUCT_INSIGHTS`;

  const mappedRows = useMemo(() => {
    return FIXED_CUSTOM_ATTRIBUTES[userPlatform].map((field) => ({
      ...field,
      value: customFieldValues?.[field.key] ?? "",
    }));
  }, [customFieldValues, userPlatform]);

  const handleCustomFieldChange = (key, value) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const getPayloadFromInputs = () => {
    const custom_attribute_mapping = {};
    FIXED_CUSTOM_ATTRIBUTES[userPlatform].forEach((field) => {
      const sourceAttributeName = (customFieldValues?.[field.key] ?? "").trim();
      if (sourceAttributeName) {
        custom_attribute_mapping[field.key] = {
          source_attribute_name: sourceAttributeName,
        };
      }
    });
    return { custom_attribute_mapping };
  };

  const fetchMapping = useCallback(async () => {
    if (!clientId) return;
    setIsLoading(true);
    try {
      const response = await axios.get(endpoint);
      const customAttributeMapping = response?.data?.custom_attribute_mapping || {};
      const values = {};
      FIXED_CUSTOM_ATTRIBUTES[userPlatform].forEach((field) => {
        values[field.key] =
          customAttributeMapping?.[field.key]?.source_attribute_name || "";
      });
      setCustomFieldValues(values);
      setMappingExists(Boolean(response?.data?.id));
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode === 404) {
        const defaultValues = {};
        FIXED_CUSTOM_ATTRIBUTES[userPlatform].forEach((field) => {
          defaultValues[field.key] = "";
        });
        setCustomFieldValues(defaultValues);
        setMappingExists(false);
      } else {
        // toast.error(
        //   "No feed mapping found"
        // );
      }
    } finally {
      setIsLoading(false);
    }
  }, [clientId, endpoint, userPlatform]);

  const handleSave = async () => {
    if (!clientId) return;
    const payload = getPayloadFromInputs();
    if (!Object.keys(payload.custom_attribute_mapping).length) {
      toast.error("Please enter at least one user custom field.");
      return;
    }
    setIsSaving(true);
    try {
      if (mappingExists) {
        await axios.put(endpoint, payload);
      } else {
        await axios.post(endpoint, payload);
      }
      toast.success("Feed mapping saved successfully.");
      setMappingExists(true);
      await fetchMapping();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save feed mapping"
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchMapping();
  }, [fetchMapping]);

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box display="flex">
        <Box
          sx={{
            width: "100%",
            px: 2,
            py: 0,
            bgcolor: "#fff",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Feed Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Map fixed attributes with user custom fields.
          </Typography>

          <TableContainer
            component={Paper}
            sx={{ border: "1px solid #E0E0E0", boxShadow: "none" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
                  <TableCell
                    sx={{ fontSize: "0.875rem", fontWeight: 600, width: "45%" }}
                  >
                    Fixed Fields
                  </TableCell>
                  <TableCell sx={{ width: "10%", textAlign: "center" }} />
                  <TableCell sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                    User Custom Fields
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappedRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell
                      sx={{
                        py: 2.5,
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        textTransform: "uppercase",
                      }}
                    >
                      {row.label}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 2.5,
                        textAlign: "center",
                        color: "#A8A8A8",
                        fontSize: "1rem",
                      }}
                    >
                      →
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.value}
                        onChange={(event) =>
                          handleCustomFieldChange(row.key, event.target.value)
                        }
                        placeholder="Enter source attribute name"
                        disabled={isLoading || isSaving}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1.5 }}
          >
            <Button
              variant="outlined"
              onClick={fetchMapping}
              disabled={isLoading || isSaving}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading || isSaving}
              sx={{ minWidth: 110 }}
            >
              {isSaving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </div>
  );
}